import React, { useState } from "react";
import { DEMENTIA_CASES } from "../data/dementiaData";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function medal(pct) {
  if (pct >= 85) return { icon: "🥇", label: "Excellent" };
  if (pct >= 60) return { icon: "🥈", label: "Good" };
  return               { icon: "🥉", label: "Keep practising" };
}

const CASES_PER_SESSION = 3;

const DementiaCaseGame = ({ onComplete, userData, saveUserData }) => {
  const [cases] = useState(() => shuffle(DEMENTIA_CASES).slice(0, CASES_PER_SESSION));
  const [caseIdx, setCaseIdx] = useState(0);

  // Phase per case: "identify" | "identify-result" | "manage" | "manage-result" | "done"
  const [phase, setPhase] = useState("identify");

  const [selectedType, setSelectedType] = useState(null);
  const [selectedManagement, setSelectedManagement] = useState({});  // id → bool
  const [managementConfirmed, setManagementConfirmed] = useState(false);

  const [results, setResults] = useState([]);

  const current = cases[caseIdx];
  const isLastCase = caseIdx === cases.length - 1;

  // ── Identify phase handlers ───────────────────────────────────────────────

  const handleSelectType = (typeId) => {
    if (phase !== "identify") return;
    setSelectedType(typeId);
  };

  const handleSubmitType = () => {
    if (!selectedType) return;
    setPhase("identify-result");
  };

  const handleProceedToManage = () => {
    setSelectedManagement({});
    setManagementConfirmed(false);
    setPhase("manage");
  };

  // ── Management phase handlers ─────────────────────────────────────────────

  const toggleManagement = (id) => {
    if (managementConfirmed) return;
    setSelectedManagement(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConfirmManagement = () => {
    setManagementConfirmed(true);
  };

  const handleAdvance = () => {
    const correctType = current.dementiaTypes.find(t => t.correct);
    const typeCorrect = selectedType === correctType.id;

    const mgmtScore = current.managementPoints.reduce((acc, pt) => {
      const selected = !!selectedManagement[pt.id];
      if (pt.correct && selected)  return acc + 10;
      if (!pt.correct && selected) return acc - 5;
      return acc;
    }, 0);

    const typeScore = typeCorrect ? 60 : 0;
    const totalScore = Math.max(0, typeScore + mgmtScore);
    const maxScore = 60 + current.managementPoints.filter(p => p.correct).length * 10;

    const updated = [...results, {
      title: current.patient.name,
      typeCorrect,
      score: totalScore,
      max: maxScore,
    }];
    setResults(updated);

    if (isLastCase) {
      const total    = updated.reduce((s, r) => s + r.score, 0);
      const maxTotal = updated.reduce((s, r) => s + r.max,   0);
      const pct      = Math.round((total / maxTotal) * 100);
      const coins    = Math.max(5,  Math.floor(pct / 5));
      const xp       = Math.max(10, Math.floor(pct / 2));
      if (saveUserData && userData) {
        saveUserData({ ...userData, coins: (userData.coins || 0) + coins, xp: (userData.xp || 0) + xp });
      }
      setPhase("done");
    } else {
      setCaseIdx(i => i + 1);
      setSelectedType(null);
      setSelectedManagement({});
      setManagementConfirmed(false);
      setPhase("identify");
    }
  };

  // ── Done screen ───────────────────────────────────────────────────────────

  if (phase === "done") {
    const total    = results.reduce((s, r) => s + r.score, 0);
    const maxTotal = results.reduce((s, r) => s + r.max,   0);
    const pct      = Math.round((total / maxTotal) * 100);
    const m        = medal(pct);
    const coins    = Math.max(5,  Math.floor(pct / 5));
    const xp       = Math.max(10, Math.floor(pct / 2));

    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">{m.icon}</div>
            <h2 className="text-2xl font-extrabold text-gray-900">{m.label}</h2>
            <p className="text-gray-500 text-sm mt-1">{cases.length} dementia cases reviewed</p>
          </div>

          <div className="space-y-2 mb-6">
            {results.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2">
                <div>
                  <span className="text-sm text-gray-700 font-medium">{r.title}</span>
                  <span className={`ml-2 text-xs ${r.typeCorrect ? "text-green-600" : "text-red-500"}`}>
                    {r.typeCorrect ? "✓ Type identified" : "✗ Type missed"}
                  </span>
                </div>
                <span className={`text-sm font-bold ${r.score === r.max ? "text-green-600" : "text-amber-600"}`}>
                  {r.score}/{r.max}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-center">
            <div className="text-3xl font-extrabold text-amber-700">{pct}%</div>
            <div className="text-sm text-amber-600">{total} / {maxTotal} points</div>
            <div className="flex justify-center gap-6 mt-3">
              <div><div className="font-bold text-yellow-600">+{coins} 🪙</div><div className="text-xs text-gray-500">Coins</div></div>
              <div><div className="font-bold text-purple-600">+{xp} ⭐</div><div className="text-xs text-gray-500">XP</div></div>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:opacity-90 transition-opacity"
          >
            Return to Care Home
          </button>
        </div>
      </div>
    );
  }

  // ── Identify phase ────────────────────────────────────────────────────────

  if (phase === "identify" || phase === "identify-result") {
    const correctType = current.dementiaTypes.find(t => t.correct);
    const chosenType  = current.dementiaTypes.find(t => t.id === selectedType);
    const typeCorrect = selectedType === correctType.id;

    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
          <div className="text-center">
            <div className="font-bold text-gray-800 text-sm">🧠 Dementia Case Review</div>
            <div className="text-xs text-gray-400">Case {caseIdx + 1} of {cases.length}</div>
          </div>
          <span className="text-xs text-gray-400 w-12 text-right">
            {phase === "identify" ? "Step 1/2" : ""}
          </span>
        </div>

        {/* Progress */}
        <div className="px-4 pt-3 pb-1 flex gap-1 flex-shrink-0">
          {cases.map((_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full ${i < caseIdx ? "bg-amber-500" : i === caseIdx ? "bg-amber-300" : "bg-gray-200"}`} />
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-3">
          {/* Patient card */}
          <div className="bg-white rounded-2xl shadow-md p-4 mb-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
                style={{ background: current.patient.color + "22", border: `2px solid ${current.patient.color}` }}>
                🧓
              </div>
              <div>
                <div className="font-bold text-gray-900">{current.patient.name}</div>
                <div className="text-xs text-gray-500">Age {current.patient.age}</div>
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">{current.patient.history}</p>
            <div className="flex flex-wrap gap-1">
              {current.patient.flags.map((f, i) => (
                <span key={i} className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full">{f}</span>
              ))}
            </div>
          </div>

          {/* Type selection */}
          <p className="text-sm font-semibold text-gray-700 mb-2">What type of dementia does this patient most likely have?</p>
          <div className="space-y-2 mb-4">
            {current.dementiaTypes.map(type => {
              let cls = "bg-white border-gray-200 text-gray-800";
              if (phase === "identify-result") {
                if (type.id === correctType.id) cls = "bg-green-100 border-green-400 text-green-800";
                else if (type.id === selectedType) cls = "bg-red-100 border-red-400 text-red-800";
                else cls = "bg-gray-50 border-gray-200 text-gray-400";
              } else if (selectedType === type.id) {
                cls = "bg-amber-100 border-amber-400 text-amber-900";
              }

              return (
                <button
                  key={type.id}
                  onClick={() => handleSelectType(type.id)}
                  disabled={phase === "identify-result"}
                  className={`w-full text-left border-2 rounded-xl px-4 py-3 text-sm font-medium transition-all ${cls} ${phase === "identify" ? "hover:border-amber-400 cursor-pointer" : "cursor-default"}`}
                >
                  {phase === "identify-result" && type.id === correctType.id && <span className="mr-1">✓</span>}
                  {phase === "identify-result" && type.id === selectedType && !typeCorrect && <span className="mr-1">✗</span>}
                  {type.name}
                </button>
              );
            })}
          </div>

          {/* Feedback after submission */}
          {phase === "identify-result" && (
            <div className={`rounded-2xl p-4 mb-4 ${typeCorrect ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              <div className={`font-bold mb-1 ${typeCorrect ? "text-green-700" : "text-red-700"}`}>
                {typeCorrect ? "✓ Correct!" : "✗ Not quite"}
              </div>
              <p className="text-sm text-gray-700">
                {typeCorrect ? current.correctTypeFeedback : chosenType?.feedback}
              </p>
              {!typeCorrect && (
                <p className="text-sm text-green-700 mt-2 font-medium">
                  The correct answer was: <span className="font-bold">{correctType.name}</span>
                  <br /><span className="font-normal text-gray-600">{current.correctTypeFeedback}</span>
                </p>
              )}
            </div>
          )}

          {/* Submit / Proceed buttons */}
          {phase === "identify" && (
            <button
              onClick={handleSubmitType}
              disabled={!selectedType}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              Submit answer
            </button>
          )}
          {phase === "identify-result" && (
            <button
              onClick={handleProceedToManage}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:opacity-90 transition-opacity"
            >
              Next: management →
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Management phase ──────────────────────────────────────────────────────

  if (phase === "manage" || phase === "manage-result") {
    const correctType = current.dementiaTypes.find(t => t.correct);

    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
          <div className="text-center">
            <div className="font-bold text-gray-800 text-sm">🧠 Dementia Case Review</div>
            <div className="text-xs text-gray-400">Case {caseIdx + 1} of {cases.length} — Step 2/2</div>
          </div>
          <span className="w-12" />
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-3">
          {/* Diagnosis reminder */}
          <div className="bg-amber-100 border border-amber-300 rounded-2xl px-4 py-3 mb-4">
            <div className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-0.5">Diagnosis</div>
            <div className="font-bold text-amber-900">{correctType.name}</div>
            <div className="text-xs text-amber-800 mt-0.5">{current.patient.name}, age {current.patient.age}</div>
          </div>

          <p className="text-sm font-semibold text-gray-700 mb-2">
            Select <span className="text-amber-600">all correct</span> management statements:
          </p>

          <div className="space-y-2 mb-4">
            {current.managementPoints.map(pt => {
              const selected = !!selectedManagement[pt.id];
              let cls = "bg-white border-gray-200";
              let badge = null;

              if (managementConfirmed) {
                if (pt.correct && selected) {
                  cls = "bg-green-100 border-green-400";
                  badge = <span className="text-xs font-bold text-green-700 ml-1">✅ Correct</span>;
                } else if (!pt.correct && selected) {
                  cls = "bg-red-100 border-red-400";
                  badge = <span className="text-xs font-bold text-red-700 ml-1">❌ Incorrect</span>;
                } else if (pt.correct && !selected) {
                  cls = "bg-amber-100 border-amber-400";
                  badge = <span className="text-xs font-bold text-amber-700 ml-1">⚠️ Missed</span>;
                } else {
                  cls = "bg-gray-50 border-gray-200 opacity-60";
                }
              } else if (selected) {
                cls = "bg-amber-100 border-amber-400";
              }

              return (
                <div
                  key={pt.id}
                  onClick={() => toggleManagement(pt.id)}
                  className={`border-2 rounded-xl px-4 py-3 text-sm transition-all ${cls} ${!managementConfirmed ? "cursor-pointer hover:border-amber-400 active:scale-98" : "cursor-default"}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${selected ? "bg-amber-500 border-amber-500" : "border-gray-300"}`}>
                      {selected && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                    <div>
                      <span className="text-gray-800">{pt.text}</span>
                      {badge}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!managementConfirmed ? (
            <button
              onClick={handleConfirmManagement}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:opacity-90 transition-opacity"
            >
              Confirm
            </button>
          ) : (
            <button
              onClick={handleAdvance}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:opacity-90 transition-opacity"
            >
              {isLastCase ? "See results →" : "Next case →"}
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default DementiaCaseGame;
